import { wire } from 'hyperhtml';

import { RenderController } from '../base';
import { ChangeEvent } from 'react';

interface SubsourceInfo {
  position: number;
  subsourceId?: number;
  supersourceId?: number;
}

interface Subsource {
  id: number;
  name: string;
}

interface State {
  defaultLessonSubsourcesMap: { [key: string]: SubsourceInfo };
  curFormIndex: number;
  supersources: { id: number, name: string }[];
  subsourcesMap: { [supersourceId: string]: Subsource[] };
}

export default class DefaultLessonSubsourcesFormController extends RenderController {
  static targets = ['defaultLessonSubsources', 'defaultLessonSubsource'];

  defaultLessonSubsourcesTarget: HTMLInputElement;
  defaultLessonSubsourceTargets: HTMLInputElement[];

  state: State = {
    defaultLessonSubsourcesMap: {},
    supersources: [],
    subsourcesMap: {},
    curFormIndex: 0,
  };

  connect() {
    ($(this.defaultLessonSubsourcesTarget) as any).sortable({
      update: this.onSortUpdate,
    });

    const supersources = JSON.parse(this.data.get('supersources'));
    const defaultLessonSubsourcesMap: { [index: string]: SubsourceInfo } = {};
    const defaultLessonSubsources: SubsourceInfo[] = JSON.parse(this.data.get('defaultLessonSubsources'));
    defaultLessonSubsources.forEach((elm, index) => {
      defaultLessonSubsourcesMap[index] = elm;
    });
    const curFormIndex = Object.keys(defaultLessonSubsourcesMap).length;
    this.setState({ supersources, defaultLessonSubsourcesMap, curFormIndex });
    Object.keys(defaultLessonSubsourcesMap).forEach((key) => {
      this.loadSubsources(defaultLessonSubsourcesMap[key].supersourceId);
    });
  }

  loadSubsources(supersourceId: string | number) {
    if (this.state.subsourcesMap[supersourceId]) return;
    $.get(`/admin/api/supersource/${supersourceId}/subsources`, (subsources: Subsource[]) => {
      const subsourcesMap = { ...this.state.subsourcesMap };
      subsourcesMap[supersourceId] = subsources;
      this.setState({ subsourcesMap });
    });
  }

  onSortUpdate = () => {
    const defaultLessonSubsourcesMap = { ...this.state.defaultLessonSubsourcesMap };
    this.defaultLessonSubsourceTargets.forEach((target, i) => {
      defaultLessonSubsourcesMap[target.dataset.formIndex].position = i;
    });
    this.setState({ defaultLessonSubsourcesMap });
  }

  onDeleteDefaultLessonSubsource = (formIndex: string) => {
    const defaultLessonSubsourcesMap = { ...this.state.defaultLessonSubsourcesMap };
    delete defaultLessonSubsourcesMap[formIndex];
    this.setState({ defaultLessonSubsourcesMap });
  }

  onAddDefaultLessonSubsource = () => {
    const defaultLessonSubsourcesMap = { ...this.state.defaultLessonSubsourcesMap };
    const positions = Object.values(defaultLessonSubsourcesMap).map(mod => mod.position);
    positions.push(-1); // just in case this is the first item, make sure a value exists
    defaultLessonSubsourcesMap[this.state.curFormIndex] = {
      position: Math.max(...positions) + 1,
      subsourceId: undefined,
    };
    this.setState({ defaultLessonSubsourcesMap, curFormIndex: this.state.curFormIndex + 1 });
  }

  onUpdateSubsource = (evt: ChangeEvent<HTMLInputElement>, formIndex: string) => {
    const val = evt.currentTarget.value;
    const defaultLessonSubsourcesMap = { ...this.state.defaultLessonSubsourcesMap };
    defaultLessonSubsourcesMap[formIndex].subsourceId = parseInt(val, 10);
    this.setState({ defaultLessonSubsourcesMap });
  }

  onUpdateSupersource = (evt: ChangeEvent<HTMLInputElement>, formIndex: string) => {
    const val = evt.currentTarget.value;
    const defaultLessonSubsourcesMap = { ...this.state.defaultLessonSubsourcesMap };
    defaultLessonSubsourcesMap[formIndex].supersourceId = parseInt(val, 10);
    this.setState({ defaultLessonSubsourcesMap });
    this.loadSubsources(val);
  }

  render() {
    const { defaultLessonSubsourcesMap } = this.state;
    const formIndices = Object.keys(defaultLessonSubsourcesMap);
    formIndices.sort((a, b) => (
      defaultLessonSubsourcesMap[a].position - defaultLessonSubsourcesMap[b].position
    ));
    const defaultLessonSubsources = formIndices.map((formIndex) => {
      const defaultLessonSubsource = defaultLessonSubsourcesMap[formIndex];
      const supersourceOpts = this.state.supersources.map(supersource => wire()`
        <option value=${supersource.id} selected=${supersource.id === defaultLessonSubsource.supersourceId}>
          ${supersource.name}
        </option>
      `);
      if (!defaultLessonSubsource.supersourceId) supersourceOpts.unshift(wire()`<option value="__None" />`);

      const subsources = this.state.subsourcesMap[defaultLessonSubsource.supersourceId] || [];
      const subsourceOpts = subsources.map(subsource => wire()`
        <option value=${subsource.id} selected=${subsource.id === defaultLessonSubsource.subsourceId}>
          ${subsource.name}
        </option>
      `);
      if (!defaultLessonSubsource.subsourceId) subsourceOpts.unshift(wire()`<option value="__None" />`);

      return wire()`
        <div
          class="row border-top bg-white"
          data-form-index=${formIndex}
          data-target="components--defaultLessonSubsourcesForm.defaultLessonSubsource"
        >
          <div class="col-12">
            <div class="row">
              <div class="col-md-5">
                <div class="form-group">
                  <label for=${`default_lesson_subsources-${formIndex}-supersource`}>
                    SUPERSOURCE
                  </label>
                  <select
                    onChange=${(evt: any) => this.onUpdateSupersource(evt, formIndex)}
                    class="form-control"
                    id=${`default_lesson_subsources-${formIndex}-supersource`}
                  >
                    ${supersourceOpts}
                  </select>
                </div>
              </div>
              <div class="col-md-5">
                <div class="form-group">
                  <label for=${`default_lesson_subsources-${formIndex}-subsource`}>
                    SUBSOURCE
                  </label>
                  <select
                    onChange=${(evt: any) => this.onUpdateSubsource(evt, formIndex)}
                    class="form-control"
                    id=${`default_lesson_subsources-${formIndex}-subsource`}
                    name=${`default_lesson_subsources-${formIndex}-subsource`}
                  >
                    ${subsourceOpts}
                  </select>
                </div>
              </div>
              <div class="col-md-2">
                <div class="form-group">
                  <label>Actions</label>
                  <div>
                    <button
                      onclick=${() => this.onDeleteDefaultLessonSubsource(formIndex)}
                      class="btn btn-danger"
                      type="button"
                    >
                      <i class="fa fa-minus-circle"></i>
                    </button>
                    <i class="fa fa-arrows-v lesson-assign pull-right" title="drag up or down" />
                  </div>
                </div>
              </div>
            </div>
            <input
              name=${`default_lesson_subsources-${formIndex}-position`}
              type="hidden"
              value=${defaultLessonSubsource.position}
            />
          </div>
        </div>
      `;
    });
    // jquery drag and drop doesn't play nicely with hyperHTML bind
    // instead just forcibly replace inner HTML each render cycle
    $(this.defaultLessonSubsourcesTarget).html(defaultLessonSubsources as any);
  }
}
